import React, { useState, useEffect } from 'react';
import { X, Save, AlertCircle, CheckCircle, Trash2 } from 'lucide-react';
import {
    stcMovementPriorityService,
    StcMovementPriorityDto,
    BulkUpdateStcMovementPriorityRequest,
    BulkDeleteResult
} from '../../../../../services/stcMovementPriorityService';
import { StcMovementTypeDto } from '../../../../../services/stcMovementTypeService';
import { StcBinTypeDto } from '../../../../../services/stcBinTypeService';
import { useTranslation } from '../../../../../contexts/I18nProvider';

interface SelectedRows {
    [key: string]: boolean;
}

interface MovementCodePrioritySidePanelProps {
    selectedRows: SelectedRows;
    data: StcMovementPriorityDto[];
    movementTypes: StcMovementTypeDto[];
    binTypes: StcBinTypeDto[];
    onClose: () => void;
    onSave: () => void;
}

interface BulkEditConfig {
    [key: string]: {
        enabled: boolean;
        value: any;
    };
}

export const MovementCodePrioritySidePanel: React.FC<MovementCodePrioritySidePanelProps> = ({
    selectedRows,
    data,
    movementTypes,
    binTypes,
    onClose,
    onSave
}) => {
    const { t } = useTranslation();
    const [bulkEditConfig, setBulkEditConfig] = useState<BulkEditConfig>({});
    const [isSaving, setIsSaving] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const [error, setError] = useState<string>('');
    const [activeTab, setActiveTab] = useState<'edit' | 'delete'>('edit');

    const selectedRowCount = Object.values(selectedRows).filter(Boolean).length;
    const selectedData = data.filter(item => selectedRows[item.id.toString()]);
    const isSingleSelection = selectedRowCount === 1;
    const singleItem = isSingleSelection ? selectedData[0] : null;

    const fields = ['movementTypeNo', 'binTypeNo', 'fromPriority', 'toPriority'];

    // Initialize bulk edit config - pre-fill when single selection
    useEffect(() => {
        const initialConfig: BulkEditConfig = {};
        fields.forEach(field => {
            if (isSingleSelection && singleItem) {
                initialConfig[field] = {
                    enabled: true,
                    value: (singleItem as any)[field] ?? 0
                };
            } else {
                initialConfig[field] = {
                    enabled: false,
                    value: field === 'movementTypeNo'
                        ? (movementTypes.length > 0 ? movementTypes[0].movementTypeNo : 1)
                        : field === 'binTypeNo'
                        ? (binTypes.length > 0 ? binTypes[0].binTypeNo : 1)
                        : 1
                };
            }
        });
        setBulkEditConfig(initialConfig);
    }, []);

    const getFieldLabel = (field: string): string => {
        const labels: { [key: string]: string } = {
            'movementTypeNo': t('manager.modules.adminMovement.movementType.label', {}, 'Bevegelseskode'),
            'binTypeNo': t('manager.modules.adminMovement.movementCodePriority.fields.binType', {}, 'Lokasjonstype'),
            'fromPriority': t('manager.modules.adminMovement.movementCodePriority.fields.fromPriority', {}, 'Fra prioritet'),
            'toPriority': t('manager.modules.adminMovement.movementCodePriority.fields.toPriority', {}, 'Til prioritet')
        };
        return labels[field] || field;
    };

    const getMovementTypeDescription = (movementTypeNo: number): string => {
        const mt = movementTypes.find(m => m.movementTypeNo === movementTypeNo);
        return mt ? mt.description1 : t('manager.modules.adminMovement.movementCodePriority.movementTypeFallback', { '0': movementTypeNo }, `Bevegelseskode ${movementTypeNo}`);
    };

    const getBinTypeDescription = (binTypeNo: number): string => {
        const bt = binTypes.find(b => b.binTypeNo === binTypeNo);
        return bt ? (bt.description1 || t('manager.modules.adminMovement.movementCodePriority.binTypeFallback', { '0': binTypeNo }, `Lokasjonstype ${binTypeNo}`)) : t('manager.modules.adminMovement.movementCodePriority.binTypeFallback', { '0': binTypeNo }, `Lokasjonstype ${binTypeNo}`);
    };

    // Fields that should only be editable for single selection (composite key parts)
    const isSingleOnlyField = (field: string): boolean => {
        return field === 'movementTypeNo' || field === 'binTypeNo';
    };

    const handleFieldToggle = (field: string, enabled: boolean) => {
        setBulkEditConfig(prev => ({
            ...prev,
            [field]: {
                ...prev[field],
                enabled
            }
        }));
    };

    const handleFieldValueChange = (field: string, value: any) => {
        let parsedValue = value;
        if (['movementTypeNo', 'binTypeNo', 'fromPriority', 'toPriority'].includes(field)) {
            parsedValue = value !== '' && value !== undefined && value !== null ? parseInt(value) : 0;
        }
        setBulkEditConfig(prev => ({
            ...prev,
            [field]: {
                ...prev[field],
                value: parsedValue
            }
        }));
    };

    const handleBulkUpdate = async () => {
        try {
            setIsSaving(true);
            setError('');

            const enabledFields = Object.entries(bulkEditConfig)
                .filter(([, config]) => config.enabled)
                .map(([field, config]) => ({ field, value: config.value }));

            if (enabledFields.length === 0) {
                setError(t('manager.modules.adminMovement.bulk.noFieldsSelected', {}, 'Ingen felter valgt for oppdatering'));
                return;
            }

            // movementTypeNo and binTypeNo can only be changed for single selection
            const hasKeyFields = enabledFields.some(f =>
                f.field === 'movementTypeNo' || f.field === 'binTypeNo'
            );
            if (hasKeyFields && selectedRowCount > 1) {
                setError(t('manager.modules.adminMovement.movementCodePriority.singleOnlyKeys', {}, 'Bevegelseskode og Lokasjonstype kan kun endres for én rad om gangen'));
                return;
            }

            // Create bulk update requests
            const bulkRequests: BulkUpdateStcMovementPriorityRequest[] = selectedData.map(item => {
                const request: BulkUpdateStcMovementPriorityRequest = {
                    companyNo: item.companyNo,
                    movementTypeNo: item.movementTypeNo,
                    binTypeNo: item.binTypeNo,
                    fromPriority: item.fromPriority,
                    toPriority: item.toPriority
                };

                // Apply enabled field changes
                enabledFields.forEach(({ field, value }) => {
                    (request as any)[field] = value;
                });

                return request;
            });

            await stcMovementPriorityService.bulkUpdate(bulkRequests);

            setShowSuccess(true);
            setTimeout(() => {
                setShowSuccess(false);
                onSave();
            }, 1500);
        } catch (err: any) {
            setError(err.message || t('manager.modules.adminMovement.errors.updateFailed', {}, 'Feil ved oppdatering'));
        } finally {
            setIsSaving(false);
        }
    };

    const handleBulkDelete = async () => {
        try {
            setIsDeleting(true);
            setError('');

            const idsToDelete = selectedData.map(item => item.id);
            const result: BulkDeleteResult = await stcMovementPriorityService.bulkDelete(idsToDelete);

            if (result.notFoundIds.length > 0) {
                setError(t('manager.modules.adminMovement.bulk.partialDelete', { '0': result.deletedCount, '1': result.requestedCount, '2': result.notFoundIds.length }, `${result.deletedCount} av ${result.requestedCount} rader ble slettet. ${result.notFoundIds.length} rader ble ikke funnet.`));
            } else {
                setShowSuccess(true);
                setTimeout(() => {
                    setShowSuccess(false);
                    onSave();
                }, 1500);
            }
        } catch (err: any) {
            setError(err.message || t('manager.modules.adminMovement.errors.deleteFailed', {}, 'Feil ved sletting'));
        } finally {
            setIsDeleting(false);
        }
    };

    const enabledFieldCount = Object.values(bulkEditConfig).filter(config => config.enabled).length;

    return (
        <div className="fixed inset-0 z-50 overflow-hidden">
            <div className="absolute inset-0 bg-black bg-opacity-25 transition-opacity" onClick={onClose} />

            <div className="absolute right-0 top-0 h-full w-96 bg-white shadow-xl border-l border-gray-200 flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50">
                    <div>
                        <h2 className="text-lg font-semibold text-gray-900">
                            {isSingleSelection
                                ? t('manager.modules.adminMovement.movementCodePriority.editTitle', {}, 'Rediger bevegelseskode, prioritet')
                                : t('manager.modules.adminMovement.bulk.title', {}, 'Masseoppdatering')}
                        </h2>
                        <p className="text-sm text-gray-600">
                            {t('manager.modules.adminMovement.movementCodePriority.rowsSelectedIn', { '0': selectedRowCount, '1': selectedRowCount === 1 ? t('manager.modules.adminMovement.bulk.row', {}, 'rad') : t('manager.modules.adminMovement.bulk.rows', {}, 'rader') }, `${selectedRowCount} ${selectedRowCount === 1 ? 'rad' : 'rader'} valgt i Bevegelseskoder, prioritet`)}
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Tab Navigation */}
                <div className="flex border-b border-gray-200">
                    <button
                        onClick={() => setActiveTab('edit')}
                        className={`flex-1 px-4 py-2 text-sm font-medium transition-colors ${
                            activeTab === 'edit'
                                ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                                : 'text-gray-600 hover:text-gray-800'
                        }`}
                    >
                        {t('common.buttons.edit', {}, 'Rediger')}
                    </button>
                    <button
                        onClick={() => setActiveTab('delete')}
                        className={`flex-1 px-4 py-2 text-sm font-medium transition-colors ${
                            activeTab === 'delete'
                                ? 'text-red-600 border-b-2 border-red-600 bg-red-50'
                                : 'text-gray-600 hover:text-gray-800'
                        }`}
                    >
                        {t('common.buttons.delete', {}, 'Slett')}
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                    {error && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                            <div className="flex items-start gap-2">
                                <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                                <p className="text-sm text-red-800">{error}</p>
                            </div>
                        </div>
                    )}

                    {activeTab === 'edit' ? (
                        <>
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                                <div className="flex items-start gap-2">
                                    <AlertCircle className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                                    <div className="text-sm text-blue-800">
                                        <p className="font-medium mb-1">
                                            {isSingleSelection
                                                ? t('manager.modules.adminMovement.bulk.editingHeader', {}, 'Redigering')
                                                : t('manager.modules.adminMovement.bulk.title', {}, 'Masseoppdatering')}
                                        </p>
                                        <p>
                                            {isSingleSelection
                                                ? t('manager.modules.adminMovement.bulk.editingHint', {}, 'Alle felt er forhåndsutfylt med gjeldende verdier. Endre det du ønsker og lagre.')
                                                : t('manager.modules.adminMovement.bulk.bulkHint', {}, 'Velg feltene du ønsker å oppdatere for alle valgte rader. Kun markerte felt vil bli endret.')}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Current values summary for single selection */}
                            {isSingleSelection && singleItem && (
                                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                                    <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">{t('manager.modules.adminMovement.bulk.currentValues', {}, 'Nåværende verdier')}</h4>
                                    <div className="space-y-1 text-sm">
                                        <div className="flex justify-between">
                                            <span className="text-gray-600">{t('manager.modules.adminMovement.movementType.label', {}, 'Bevegelseskode')}:</span>
                                            <span className="font-medium text-gray-900">{getMovementTypeDescription(singleItem.movementTypeNo)}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-gray-600">{t('manager.modules.adminMovement.movementCodePriority.fields.binType', {}, 'Lokasjonstype')}:</span>
                                            <span className="font-medium text-gray-900">{getBinTypeDescription(singleItem.binTypeNo)}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-gray-600">{t('manager.modules.adminMovement.movementCodePriority.fields.fromPriority', {}, 'Fra prioritet')}:</span>
                                            <span className="font-medium text-gray-900">{singleItem.fromPriority}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-gray-600">{t('manager.modules.adminMovement.movementCodePriority.fields.toPriority', {}, 'Til prioritet')}:</span>
                                            <span className="font-medium text-gray-900">{singleItem.toPriority}</span>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Field Editor */}
                            <div className="space-y-3">
                                <h3 className="text-sm font-medium text-gray-900">{t('manager.modules.adminMovement.bulk.fieldsToUpdate', {}, 'Felter som skal oppdateres')}</h3>

                                {fields.map(field => {
                                    const config = bulkEditConfig[field];
                                    const singleOnly = isSingleOnlyField(field);
                                    const isDisabled = singleOnly && selectedRowCount > 1;

                                    if (!config) return null;

                                    return (
                                        <div
                                            key={field}
                                            className={`border rounded-lg p-3 transition-colors ${
                                                isDisabled
                                                    ? 'border-gray-100 bg-gray-50 opacity-60'
                                                    : config.enabled
                                                    ? 'border-blue-200 bg-blue-50'
                                                    : 'border-gray-200 bg-white'
                                            }`}
                                        >
                                            {/* Field Toggle */}
                                            <div className="flex items-center justify-between">
                                                <label className={`flex items-center gap-2 ${isDisabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                                                    <input
                                                        type="checkbox"
                                                        checked={config.enabled}
                                                        onChange={(e) => handleFieldToggle(field, e.target.checked)}
                                                        disabled={isDisabled}
                                                        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                                                    />
                                                    <span className="text-sm font-medium text-gray-700">
                                                        {getFieldLabel(field)}
                                                    </span>
                                                </label>
                                                {singleOnly && selectedRowCount > 1 && (
                                                    <span className="text-xs text-amber-600">{t('manager.modules.adminMovement.bulk.singleSelectionOnly', {}, 'Kun ved enkeltvalg')}</span>
                                                )}
                                            </div>

                                            {/* Field Value Input */}
                                            {config.enabled && !isDisabled && (
                                                <div className="mt-2">
                                                    {field === 'movementTypeNo' ? (
                                                        <select
                                                            value={config.value}
                                                            onChange={(e) => handleFieldValueChange(field, e.target.value)}
                                                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                                                        >
                                                            {movementTypes.map(mt => (
                                                                <option key={mt.movementTypeNo} value={mt.movementTypeNo}>
                                                                    {mt.description1} ({mt.movementTypeNo})
                                                                </option>
                                                            ))}
                                                        </select>
                                                    ) : field === 'binTypeNo' ? (
                                                        <select
                                                            value={config.value}
                                                            onChange={(e) => handleFieldValueChange(field, e.target.value)}
                                                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                                                        >
                                                            {binTypes.map(bt => (
                                                                <option key={bt.binTypeNo} value={bt.binTypeNo}>
                                                                    {bt.description1 || t('manager.modules.adminMovement.movementCodePriority.binTypeFallback', { '0': bt.binTypeNo }, `Lokasjonstype ${bt.binTypeNo}`)} ({bt.binTypeNo})
                                                                </option>
                                                            ))}
                                                        </select>
                                                    ) : (
                                                        <input
                                                            type="number"
                                                            min="0"
                                                            value={config.value}
                                                            onChange={(e) => handleFieldValueChange(field, e.target.value)}
                                                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                            placeholder={t('manager.modules.adminMovement.bulk.newValueFor', { '0': getFieldLabel(field) }, `Ny verdi for ${getFieldLabel(field)}`)}
                                                        />
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Summary */}
                            {enabledFieldCount > 0 && (
                                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                                    <div className="flex items-start gap-2">
                                        <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                                        <div className="text-sm text-green-800">
                                            <p className="font-medium">{t('manager.modules.adminMovement.bulk.readyToSave', {}, 'Klar til å lagre')}</p>
                                            <p>
                                                {t('manager.modules.adminMovement.bulk.fieldsWillBeUpdated', { '0': enabledFieldCount, '1': selectedRowCount, '2': selectedRowCount === 1 ? t('manager.modules.adminMovement.bulk.row', {}, 'rad') : t('manager.modules.adminMovement.bulk.rows', {}, 'rader') }, `${enabledFieldCount} felt vil bli oppdatert for ${selectedRowCount} ${selectedRowCount === 1 ? 'rad' : 'rader'}`)}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </>
                    ) : (
                        <>
                            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                                <div className="flex items-start gap-2">
                                    <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                                    <div className="text-sm text-red-800">
                                        <p className="font-medium mb-1">{t('manager.modules.adminMovement.bulk.deleteHeader', {}, 'Massesletting')}</p>
                                        <p>{t('manager.modules.adminMovement.bulk.deleteWarning', { '0': selectedRowCount, '1': selectedRowCount === 1 ? t('manager.modules.adminMovement.bulk.row', {}, 'rad') : t('manager.modules.adminMovement.bulk.rows', {}, 'rader') }, `Du er i ferd med å slette ${selectedRowCount} ${selectedRowCount === 1 ? 'rad' : 'rader'}. Denne handlingen kan ikke angres.`)}</p>
                                    </div>
                                </div>
                            </div>

                            {/* Selected Items List */}
                            <div className="space-y-2">
                                <h3 className="text-sm font-medium text-gray-900">{t('manager.modules.adminMovement.bulk.rowsToDelete', {}, 'Rader som vil bli slettet:')}</h3>
                                <div className="max-h-40 overflow-y-auto space-y-1">
                                    {selectedData.map(item => (
                                        <div key={item.id} className="text-xs bg-gray-50 p-2 rounded border">
                                            {getMovementTypeDescription(item.movementTypeNo)}, {getBinTypeDescription(item.binTypeNo)}
                                            {' '}{t('manager.modules.adminMovement.movementCodePriority.priorityRange', { '0': item.fromPriority, '1': item.toPriority }, `(Fra ${item.fromPriority} til ${item.toPriority})`)}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </>
                    )}
                </div>

                {/* Footer */}
                <div className="border-t border-gray-200 p-4 bg-gray-50">
                    <div className="flex items-center justify-between gap-3">
                        <button
                            onClick={onClose}
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                        >
                            {t('common.buttons.cancel', {}, 'Avbryt')}
                        </button>

                        {activeTab === 'edit' ? (
                            <button
                                onClick={handleBulkUpdate}
                                disabled={enabledFieldCount === 0 || isSaving}
                                className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                                    enabledFieldCount === 0 || isSaving
                                        ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                        : 'bg-blue-600 text-white hover:bg-blue-700'
                                }`}
                            >
                                {isSaving ? (
                                    <>
                                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                        {t('common.status.saving', {}, 'Lagrer...')}
                                    </>
                                ) : showSuccess ? (
                                    <>
                                        <CheckCircle className="w-4 h-4" />
                                        {t('manager.modules.adminMovement.bulk.saved', {}, 'Lagret!')}
                                    </>
                                ) : (
                                    <>
                                        <Save className="w-4 h-4" />
                                        {t('manager.modules.adminMovement.bulk.saveChanges', {}, 'Lagre endringer')}
                                    </>
                                )}
                            </button>
                        ) : (
                            <button
                                onClick={handleBulkDelete}
                                disabled={isDeleting}
                                className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                                    isDeleting
                                        ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                        : 'bg-red-600 text-white hover:bg-red-700'
                                }`}
                            >
                                {isDeleting ? (
                                    <>
                                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                        {t('manager.modules.adminMovement.bulk.deleting', {}, 'Sletter...')}
                                    </>
                                ) : showSuccess ? (
                                    <>
                                        <CheckCircle className="w-4 h-4" />
                                        {t('manager.modules.adminMovement.bulk.deleted', {}, 'Slettet!')}
                                    </>
                                ) : (
                                    <>
                                        <Trash2 className="w-4 h-4" />
                                        {t('manager.modules.adminMovement.bulk.deleteCount', { '0': selectedRowCount, '1': selectedRowCount === 1 ? t('manager.modules.adminMovement.bulk.row', {}, 'rad') : t('manager.modules.adminMovement.bulk.rows', {}, 'rader') }, `Slett ${selectedRowCount} ${selectedRowCount === 1 ? 'rad' : 'rader'}`)}
                                    </>
                                )}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};
